/**
 * BulkManageDrawer — Editable table for all spaces + bookable date compatibility checker (Legacy version)
 *
 * DROP INTO: src/components/Settings/BulkManageDrawer.jsx
 *
 * PROPS (passed from spaces.jsx):
 *   open, onClose, locationId, companyId, setSnackBar
 *   + props.firebase (injected by withFirebase HOC)
 */
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import {
   Alert,
   Autocomplete,
   Box,
   Button,
   Checkbox,
   CircularProgress,
   Dialog,
   DialogActions,
   DialogContent,
   DialogContentText,
   DialogTitle,
   Drawer,
   IconButton,
   MenuItem,
   Select,
   Stack,
   Tab,
   Table,
   TableBody,
   TableCell,
   TableContainer,
   TableHead,
   TablePagination,
   TableRow,
   Tabs,
   TextField,
   Typography,
} from '@mui/material';
import firebase from 'firebase/app';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { spaceTypes } from '../../constants/data';
import { isSpaceAvailableOnDate } from '../../utils/spaceAvailability';
import { withFirebase } from '../Firebase';
import { naturalCompare } from '../Spaces/utils';

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const SPACE_AMENITIES = [
   'Desk storage',
   'Ergonomic chair',
   'External monitor',
   'Height adjustable desk',
   'Keyboard + mouse',
   'Lockable door',
   'Multi screen',
   'Multiple seat',
   'Printer',
   'Projector',
   'Window view',
   'Tv monitor',
   'White boards',
];

const isRowDirty = (space) => {
   const o = space._original;
   return (
      space.name !== o.name ||
      space.description !== o.description ||
      (space.seats ?? 0) !== (o.seats ?? 0) ||
      space.type !== o.type ||
      (space.parent ?? '') !== (o.parent ?? '') ||
      space.bookable !== o.bookable ||
      space.timePeriod.allDay !== o.timePeriod.allDay ||
      space.timePeriod.ampm !== o.timePeriod.ampm ||
      space.timePeriod.hourly !== o.timePeriod.hourly ||
      space.approvalRequired !== o.approvalRequired ||
      (space.amenities ?? []).join(',') !== (o.amenities ?? []).join(',') ||
      (space.backgroundColor ?? '') !== (o.backgroundColor ?? '') ||
      !!space.ignoreWhenChangingStatus !== !!o.ignoreWhenChangingStatus ||
      (space.bookableFrom ?? '') !== (o.bookableFrom ?? '') ||
      (space.bookableUntil ?? '') !== (o.bookableUntil ?? '') ||
      !!space.archive !== !!o.archive
   );
};

const formatDateRef = (ref) => {
   if (!ref || ref.length !== 8) return ref;
   const y = ref.slice(0, 4);
   const m = parseInt(ref.slice(4, 6), 10) + 1;
   const d = ref.slice(6, 8);
   return `${d}/${String(m).padStart(2, '0')}/${y}`;
};

/** Convert internal YYYYMMDD (zero-based month) → display DDMMYYYY (1-based month). */
const internalToDisplay = (ref) => {
   if (!ref || ref.length !== 8) return ref || '';
   const yyyy = ref.slice(0, 4);
   const zeroMonth = parseInt(ref.slice(4, 6), 10);
   const dd = ref.slice(6, 8);
   const mm = String(zeroMonth + 1).padStart(2, '0');
   return `${dd}${mm}${yyyy}`;
};

/** Convert display DDMMYYYY (1-based month) → internal YYYYMMDD (zero-based month).
 *  Returns null if invalid. */
const displayToInternal = (val) => {
   const v = (val ?? '').trim();
   if (!v) return '';
   if (v.length !== 8 || !/^\d{8}$/.test(v)) return null;
   const dd = v.slice(0, 2);
   const mm = v.slice(2, 4);
   const yyyy = v.slice(4, 8);
   const day = parseInt(dd, 10);
   const month1 = parseInt(mm, 10);
   const year = parseInt(yyyy, 10);
   if (month1 < 1 || month1 > 12 || day < 1 || day > 31) return null;
   const dateObj = new Date(year, month1 - 1, day);
   if (
      dateObj.getFullYear() !== year ||
      dateObj.getMonth() !== month1 - 1 ||
      dateObj.getDate() !== day
   )
      return null;
   return `${yyyy}${String(month1 - 1).padStart(2, '0')}${dd}`;
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const BulkManageDrawerBase = (props) => {
   const { open, onClose, locationId, companyId, setSnackBar } = props;
   const fb = props.firebase;

   const [spaces, setSpaces] = useState([]);
   const [loading, setLoading] = useState(true);
   const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

   // Bookable date compatibility view
   const [compatView, setCompatView] = useState(false);
   const [compatLoading, setCompatLoading] = useState(false);
   const [violations, setViolations] = useState([]);
   const [deleting, setDeleting] = useState(false);

   // Archive filter
   const [archiveFilter, setArchiveFilter] = useState('active');

   // Pagination
   const [page, setPage] = useState(0);
   const [rowsPerPage, setRowsPerPage] = useState(25);

   /* ---- Areas for parent select ---- */
   const areas = useMemo(() => spaces.filter((s) => s.type === 'area'), [spaces]);

   /* ---- Dirty tracking ---- */
   const hasDirtyRows = useMemo(() => spaces.some((s) => s._dirty), [spaces]);

   /* ---- Load spaces ---- */
   const loadSpaces = useCallback(
      async (isArchived = false) => {
         setLoading(true);
         try {
            const snap = await fb.db
               .collection(`location/${locationId}/spaces`)
               .where('archive', '==', isArchived)
               .get();

            const data = [];
            snap.forEach((d) => {
               const raw = { sid: d.id, ...d.data() };
               // Convert internal dates to display format for editing
               raw.bookableFrom = internalToDisplay(raw.bookableFrom ?? '');
               raw.bookableUntil = internalToDisplay(raw.bookableUntil ?? '');
               data.push({
                  ...raw,
                  _original: {
                     ...raw,
                     timePeriod: { ...raw.timePeriod },
                     amenities: [...(raw.amenities ?? [])],
                  },
                  _dirty: false,
                  _saving: false,
               });
            });
            data.sort((a, b) => {
               if (a.order !== undefined && b.order !== undefined)
                  return a.order - b.order;
               return naturalCompare(a.name, b.name);
            });
            setSpaces(data);
            setPage(0);
         } catch (err) {
            console.error('[BulkManageDrawer] Load error:', err);
         } finally {
            setLoading(false);
         }
      },
      [locationId, fb],
   );

   useEffect(() => {
      if (open) {
         setArchiveFilter('active');
         loadSpaces(false);
         setCompatView(false);
         setViolations([]);
      }
   }, [open, loadSpaces]);

   /* ---- Paginated slice ---- */
   const paginatedSpaces = useMemo(
      () => spaces.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
      [spaces, page, rowsPerPage],
   );

   /* ---- Tab switch ---- */
   const handleTabChange = (_e, val) => {
      setArchiveFilter(val);
      loadSpaces(val === 'archived');
   };

   /* ---- Close handling ---- */
   const handleCloseAttempt = () => {
      if (hasDirtyRows) {
         setDiscardDialogOpen(true);
      } else {
         onClose();
      }
   };

   /* ---- Field updater ---- */
   const updateField = (sid, field, value) => {
      setSpaces((prev) =>
         prev.map((s) => {
            if (s.sid !== sid) return s;
            let updated;
            if (field.startsWith('timePeriod.')) {
               const tpField = field.split('.')[1];
               updated = { ...s, timePeriod: { ...s.timePeriod, [tpField]: value } };
            } else {
               updated = { ...s, [field]: value };
            }
            updated._dirty = isRowDirty(updated);
            return updated;
         }),
      );
   };

   /* ---- Save single row ---- */
   const saveRow = async (space) => {
      // Validate bookable dates before saving
      const internalFrom = displayToInternal(space.bookableFrom);
      const internalUntil = displayToInternal(space.bookableUntil);
      if (internalFrom === null) {
         setSnackBar({
            display: true,
            type: 'error',
            message: `"${space.name}" has an invalid Bookable From date. Use DDMMYYYY.`,
         });
         return;
      }
      if (internalUntil === null) {
         setSnackBar({
            display: true,
            type: 'error',
            message: `"${space.name}" has an invalid Bookable Until date. Use DDMMYYYY.`,
         });
         return;
      }

      setSpaces((prev) =>
         prev.map((s) => (s.sid === space.sid ? { ...s, _saving: true } : s)),
      );
      try {
         const data = {
            name: space.name.trim(),
            description: space.description ?? '',
            seats: space.seats ? Number(space.seats) : 0,
            type: space.type,
            parent: space.parent ?? '',
            bookable: space.bookable,
            timePeriod: space.timePeriod,
            approvalRequired: space.approvalRequired,
            amenities: space.amenities ?? [],
            archive: !!space.archive,
            backgroundColor: space.backgroundColor ?? '',
            ignoreWhenChangingStatus: space.ignoreWhenChangingStatus ?? false,
            bookableFrom: internalFrom,
            bookableUntil: internalUntil,
         };
         await fb.db
            .doc(`location/${locationId}/spaces/${space.sid}`)
            .set(data, { merge: true });

         // If archive status changed, remove from current list
         const archiveChanged = !!space.archive !== !!space._original.archive;
         if (archiveChanged) {
            setSpaces((prev) => prev.filter((s) => s.sid !== space.sid));
            setSnackBar({
               display: true,
               type: 'success',
               message: `"${space.name}" ${space.archive ? 'archived' : 'restored'}`,
            });
         } else {
            setSpaces((prev) =>
               prev.map((s) => {
                  if (s.sid !== space.sid) return s;
                  return {
                     ...s,
                     _original: {
                        ...s,
                        timePeriod: { ...s.timePeriod },
                        amenities: [...(s.amenities ?? [])],
                     },
                     _dirty: false,
                     _saving: false,
                  };
               }),
            );
            setSnackBar({
               display: true,
               type: 'success',
               message: `"${space.name}" saved`,
            });
         }
      } catch (err) {
         console.error('[BulkManageDrawer] Save error:', err);
         setSnackBar({
            display: true,
            type: 'error',
            message: `Failed to save "${space.name}"`,
         });
         setSpaces((prev) =>
            prev.map((s) => (s.sid === space.sid ? { ...s, _saving: false } : s)),
         );
      }
   };

   /* ---- Save all dirty rows ---- */
   const saveAllDirty = async () => {
      const dirtyRows = spaces.filter((s) => s._dirty);
      for (const row of dirtyRows) {
         await saveRow(row);
      }
   };

   /* ================================================================ */
   /*  Bookable Date Compatibility Check                               */
   /* ================================================================ */

   const runCompatibilityCheck = async () => {
      setCompatLoading(true);
      setCompatView(true);
      setViolations([]);

      try {
         const restricted = spaces.filter((s) => {
            const intFrom = displayToInternal(s.bookableFrom);
            const intUntil = displayToInternal(s.bookableUntil);
            return intFrom || intUntil;
         });

         if (restricted.length === 0) {
            setCompatLoading(false);
            return;
         }

         const found = [];

         // Get ALL spaceBookings date documents
         const allDateDocs = await fb.db
            .collection(`location/${locationId}/spaceBookings`)
            .get();

         allDateDocs.forEach((dateDoc) => {
            const dateRef = dateDoc.id;
            const dateData = dateDoc.data();

            for (const space of restricted) {
               // isSpaceAvailableOnDate expects internal YYYYMMDD format
               const spaceWithInternalDates = {
                  ...space,
                  bookableFrom: displayToInternal(space.bookableFrom) || '',
                  bookableUntil: displayToInternal(space.bookableUntil) || '',
               };
               if (isSpaceAvailableOnDate(spaceWithInternalDates, dateRef)) continue;

               const spaceBookings = dateData[space.sid];
               if (!spaceBookings?.bookings) continue;

               for (const [bid, booking] of Object.entries(spaceBookings.bookings)) {
                  found.push({
                     sid: space.sid,
                     spaceName: space.name,
                     date: dateRef,
                     bid,
                     ownerId: booking.ownerId ?? '',
                     ownerName: '',
                     type: booking.type ?? '',
                     seats: booking.seats ?? 1,
                     selected: false,
                  });
               }
            }
         });

         // Resolve user names
         const uniqueOwners = [...new Set(found.map((v) => v.ownerId).filter(Boolean))];
         const ownerNames = new Map();

         for (const uid of uniqueOwners) {
            try {
               const personDoc = await fb.db
                  .doc(`company/${companyId}/people/${uid}`)
                  .get();
               if (personDoc.exists) {
                  const d = personDoc.data();
                  ownerNames.set(
                     uid,
                     `${d.name?.firstName ?? ''} ${d.name?.lastName ?? ''}`.trim(),
                  );
               }
            } catch {
               // Ignore lookup errors
            }
         }

         found.forEach((v) => {
            v.ownerName = ownerNames.get(v.ownerId) || v.ownerId;
         });

         found.sort((a, b) => {
            const nameCompare = naturalCompare(a.spaceName, b.spaceName);
            if (nameCompare !== 0) return nameCompare;
            return a.date.localeCompare(b.date);
         });

         setViolations(found);
      } catch (err) {
         console.error('[BulkManageDrawer] Compatibility check error:', err);
         setSnackBar({
            display: true,
            type: 'error',
            message: 'Failed to check bookable date compatibility',
         });
      } finally {
         setCompatLoading(false);
      }
   };

   /* ---- Selection helpers ---- */
   const toggleViolation = (idx) => {
      setViolations((prev) =>
         prev.map((v, i) => (i === idx ? { ...v, selected: !v.selected } : v)),
      );
   };
   const selectAll = () =>
      setViolations((prev) => prev.map((v) => ({ ...v, selected: true })));
   const deselectAll = () =>
      setViolations((prev) => prev.map((v) => ({ ...v, selected: false })));

   const selectedCount = violations.filter((v) => v.selected).length;

   /* ---- Delete selected violations ---- */
   const deleteSelectedViolations = async () => {
      const toDelete = violations.filter((v) => v.selected);
      if (toDelete.length === 0) return;
      setDeleting(true);

      try {
         const decrement = firebase.firestore.FieldValue.increment(-1);
         const deleteFieldValue = firebase.firestore.FieldValue.delete();
         const BATCH_LIMIT = 125;

         for (let i = 0; i < toDelete.length; i += BATCH_LIMIT) {
            const chunk = toDelete.slice(i, i + BATCH_LIMIT);
            const batch = fb.db.batch();

            for (const v of chunk) {
               const sbRef = fb.db.doc(`location/${locationId}/spaceBookings/${v.date}`);
               const ssRef = fb.db.doc(
                  `location/${locationId}/spaces/${v.sid}/schedule/${v.date}`,
               );
               const locRef = fb.db.doc(`location/${locationId}/schedule/${v.date}`);
               const usrRef = fb.db.doc(
                  `company/${companyId}/people/${v.ownerId}/schedule/${v.date}`,
               );

               batch.set(
                  sbRef,
                  {
                     [v.sid]: {
                        bookingNo: decrement,
                        bookings: { [v.bid]: deleteFieldValue },
                     },
                  },
                  { merge: true },
               );
               batch.set(
                  ssRef,
                  { bookingNo: decrement, bookings: { [v.bid]: deleteFieldValue } },
                  { merge: true },
               );
               batch.set(
                  locRef,
                  {
                     [v.ownerId]: {
                        bookingNo: decrement,
                        bookings: { [v.bid]: deleteFieldValue },
                     },
                  },
                  { merge: true },
               );
               batch.set(
                  usrRef,
                  { bookingNo: decrement, bookings: { [v.bid]: deleteFieldValue } },
                  { merge: true },
               );
            }
            await batch.commit();
         }

         setSnackBar({
            display: true,
            type: 'success',
            message: `Deleted ${toDelete.length} violating booking(s)`,
         });

         await runCompatibilityCheck();
      } catch (err) {
         console.error('[BulkManageDrawer] Delete error:', err);
         setSnackBar({
            display: true,
            type: 'error',
            message: 'Failed to delete some bookings',
         });
      } finally {
         setDeleting(false);
      }
   };

   /* ================================================================ */
   /*  Render                                                           */
   /* ================================================================ */

   return (
      <>
         <Drawer
            anchor="right"
            open={open}
            onClose={handleCloseAttempt}
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
            PaperProps={{ sx: { width: '75vw' } }}
         >
            {/* Header */}
            <Stack
               direction="row"
               justifyContent="space-between"
               alignItems="center"
               sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
            >
               <Typography variant="h6">Bulk Manage Spaces</Typography>
               <Stack direction="row" spacing={1} alignItems="center">
                  {!compatView && hasDirtyRows && (
                     <Button
                        variant="contained"
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={saveAllDirty}
                     >
                        Save All Changes
                     </Button>
                  )}
                  {!compatView && (
                     <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SearchIcon />}
                        onClick={runCompatibilityCheck}
                     >
                        Check Bookable Date Compatibility
                     </Button>
                  )}
                  {compatView && (
                     <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => setCompatView(false)}
                     >
                        Back to Spaces
                     </Button>
                  )}
                  <IconButton onClick={handleCloseAttempt} size="small">
                     <CloseIcon />
                  </IconButton>
               </Stack>
            </Stack>

            {/* Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
               {loading ? (
                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '50vh',
                        gap: 2,
                     }}
                  >
                     <CircularProgress size={48} />
                     <Typography variant="body2" color="text.secondary">
                        Loading spaces…
                     </Typography>
                  </Box>
               ) : compatView ? (
                  /* ---- Compatibility violations view ---- */
                  <Box>
                     {compatLoading ? (
                        <Box
                           sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: '40vh',
                              gap: 2,
                           }}
                        >
                           <CircularProgress size={48} />
                           <Typography variant="body2" color="text.secondary">
                              Checking compatibility…
                           </Typography>
                        </Box>
                     ) : violations.length === 0 ? (
                        <Alert severity="success" sx={{ mt: 2 }}>
                           No bookings found that violate bookable date restrictions.
                        </Alert>
                     ) : (
                        <Box>
                           <Stack
                              direction="row"
                              spacing={2}
                              alignItems="center"
                              sx={{ mb: 2 }}
                           >
                              <Typography variant="body2">
                                 Found <strong>{violations.length}</strong> violating
                                 booking(s)
                              </Typography>
                              <Button size="small" onClick={selectAll}>
                                 Select All
                              </Button>
                              <Button size="small" onClick={deselectAll}>
                                 Deselect All
                              </Button>
                              {selectedCount > 0 && (
                                 <Button
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    onClick={deleteSelectedViolations}
                                    disabled={deleting}
                                    startIcon={
                                       deleting ? (
                                          <CircularProgress size={16} />
                                       ) : undefined
                                    }
                                 >
                                    {deleting
                                       ? 'Deleting…'
                                       : `Delete ${selectedCount} Selected`}
                                 </Button>
                              )}
                           </Stack>
                           <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)' }}>
                              <Table size="small" stickyHeader>
                                 <TableHead>
                                    <TableRow>
                                       <TableCell padding="checkbox" />
                                       <TableCell sx={{ fontWeight: 'bold' }}>
                                          Space
                                       </TableCell>
                                       <TableCell sx={{ fontWeight: 'bold' }}>
                                          Date
                                       </TableCell>
                                       <TableCell sx={{ fontWeight: 'bold' }}>
                                          Type
                                       </TableCell>
                                       <TableCell sx={{ fontWeight: 'bold' }}>
                                          User
                                       </TableCell>
                                       <TableCell sx={{ fontWeight: 'bold' }}>
                                          Booking ID
                                       </TableCell>
                                    </TableRow>
                                 </TableHead>
                                 <TableBody>
                                    {violations.map((v, idx) => (
                                       <TableRow key={`${v.bid}-${v.date}`}>
                                          <TableCell padding="checkbox">
                                             <Checkbox
                                                checked={v.selected}
                                                onChange={() => toggleViolation(idx)}
                                             />
                                          </TableCell>
                                          <TableCell>{v.spaceName}</TableCell>
                                          <TableCell>{formatDateRef(v.date)}</TableCell>
                                          <TableCell>{v.type}</TableCell>
                                          <TableCell>{v.ownerName}</TableCell>
                                          <TableCell>
                                             <Typography variant="caption">
                                                {v.bid}
                                             </Typography>
                                          </TableCell>
                                       </TableRow>
                                    ))}
                                 </TableBody>
                              </Table>
                           </TableContainer>
                        </Box>
                     )}
                  </Box>
               ) : (
                  /* ---- Editable spaces table ---- */
                  <>
                     <Tabs
                        value={archiveFilter}
                        onChange={handleTabChange}
                        sx={{ mb: 2 }}
                     >
                        <Tab label="Active" value="active" />
                        <Tab label="Archived" value="archived" />
                     </Tabs>
                     <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
                        <Table size="small" stickyHeader>
                           <TableHead>
                              <TableRow>
                                 <TableCell sx={{ fontWeight: 'bold', minWidth: 140 }}>
                                    Name
                                 </TableCell>
                                 <TableCell sx={{ fontWeight: 'bold', minWidth: 140 }}>
                                    Description
                                 </TableCell>
                                 <TableCell sx={{ fontWeight: 'bold', minWidth: 70 }}>
                                    Seats
                                 </TableCell>
                                 <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>
                                    Type
                                 </TableCell>
                                 <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>
                                    Parent
                                 </TableCell>
                                 <TableCell sx={{ fontWeight: 'bold' }}>
                                    Bookable
                                 </TableCell>
                                 <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }}>
                                    Amenities
                                 </TableCell>
                                 <TableCell sx={{ fontWeight: 'bold', minWidth: 60 }}>
                                    Colour
                                 </TableCell>
                                 <TableCell sx={{ fontWeight: 'bold' }}>
                                    Ignore Status
                                 </TableCell>
                                 <TableCell sx={{ fontWeight: 'bold', minWidth: 110 }}>
                                    Bookable From
                                 </TableCell>
                                 <TableCell sx={{ fontWeight: 'bold', minWidth: 110 }}>
                                    Bookable Until
                                 </TableCell>
                                 <TableCell sx={{ fontWeight: 'bold' }}>
                                    Archive
                                 </TableCell>
                                 <TableCell sx={{ fontWeight: 'bold', minWidth: 80 }}>
                                    Actions
                                 </TableCell>
                              </TableRow>
                           </TableHead>
                           <TableBody>
                              {paginatedSpaces.map((space) => (
                                 <TableRow
                                    key={space.sid}
                                    sx={{
                                       bgcolor: space._dirty ? 'action.hover' : undefined,
                                    }}
                                 >
                                    {/* Name */}
                                    <TableCell>
                                       <TextField
                                          size="small"
                                          variant="standard"
                                          value={space.name}
                                          onChange={(e) =>
                                             updateField(
                                                space.sid,
                                                'name',
                                                e.target.value,
                                             )
                                          }
                                          fullWidth
                                       />
                                    </TableCell>
                                    {/* Description */}
                                    <TableCell>
                                       <TextField
                                          size="small"
                                          variant="standard"
                                          value={space.description ?? ''}
                                          onChange={(e) =>
                                             updateField(
                                                space.sid,
                                                'description',
                                                e.target.value,
                                             )
                                          }
                                          fullWidth
                                          multiline
                                          maxRows={2}
                                       />
                                    </TableCell>
                                    {/* Seats */}
                                    <TableCell>
                                       <TextField
                                          size="small"
                                          variant="standard"
                                          type="number"
                                          value={space.seats ?? 0}
                                          onChange={(e) =>
                                             updateField(
                                                space.sid,
                                                'seats',
                                                Number(e.target.value),
                                             )
                                          }
                                          sx={{ width: 60 }}
                                          inputProps={{ min: 0 }}
                                       />
                                    </TableCell>
                                    {/* Type */}
                                    <TableCell>
                                       <Select
                                          size="small"
                                          variant="standard"
                                          value={space.type}
                                          onChange={(e) =>
                                             updateField(
                                                space.sid,
                                                'type',
                                                e.target.value,
                                             )
                                          }
                                          fullWidth
                                       >
                                          {Object.entries(spaceTypes).map(
                                             ([k, label]) => (
                                                <MenuItem key={k} value={k}>
                                                   {label}
                                                </MenuItem>
                                             ),
                                          )}
                                       </Select>
                                    </TableCell>
                                    {/* Parent */}
                                    <TableCell>
                                       <Select
                                          size="small"
                                          variant="standard"
                                          value={space.parent ?? ''}
                                          onChange={(e) =>
                                             updateField(
                                                space.sid,
                                                'parent',
                                                e.target.value,
                                             )
                                          }
                                          displayEmpty
                                          fullWidth
                                       >
                                          <MenuItem value="">
                                             <em>None</em>
                                          </MenuItem>
                                          {areas
                                             .filter((a) => a.sid !== space.sid)
                                             .map((a) => (
                                                <MenuItem key={a.sid} value={a.sid}>
                                                   {a.name}
                                                </MenuItem>
                                             ))}
                                       </Select>
                                    </TableCell>
                                    {/* Bookable */}
                                    <TableCell align="center">
                                       <Checkbox
                                          checked={space.bookable}
                                          onChange={(e) =>
                                             updateField(
                                                space.sid,
                                                'bookable',
                                                e.target.checked,
                                             )
                                          }
                                          size="small"
                                       />
                                    </TableCell>
                                    {/* Amenities */}
                                    <TableCell>
                                       <Autocomplete
                                          multiple
                                          size="small"
                                          options={SPACE_AMENITIES}
                                          disableCloseOnSelect
                                          value={space.amenities ?? []}
                                          onChange={(_e, newValue) =>
                                             updateField(space.sid, 'amenities', newValue)
                                          }
                                          renderInput={(params) => (
                                             <TextField
                                                {...params}
                                                variant="standard"
                                                placeholder="Select"
                                             />
                                          )}
                                          sx={{ minWidth: 160 }}
                                       />
                                    </TableCell>
                                    {/* Colour */}
                                    <TableCell>
                                       <input
                                          type="color"
                                          value={space.backgroundColor || '#ffffff'}
                                          onChange={(e) =>
                                             updateField(
                                                space.sid,
                                                'backgroundColor',
                                                e.target.value,
                                             )
                                          }
                                          style={{
                                             width: 32,
                                             height: 24,
                                             border: '1px solid #ccc',
                                             cursor: 'pointer',
                                             padding: 0,
                                          }}
                                       />
                                    </TableCell>
                                    {/* Ignore When Changing Status */}
                                    <TableCell align="center">
                                       <Checkbox
                                          checked={!!space.ignoreWhenChangingStatus}
                                          onChange={(e) =>
                                             updateField(
                                                space.sid,
                                                'ignoreWhenChangingStatus',
                                                e.target.checked,
                                             )
                                          }
                                          size="small"
                                       />
                                    </TableCell>
                                    {/* Bookable From */}
                                    <TableCell>
                                       <TextField
                                          size="small"
                                          variant="standard"
                                          value={space.bookableFrom ?? ''}
                                          onChange={(e) =>
                                             updateField(
                                                space.sid,
                                                'bookableFrom',
                                                e.target.value,
                                             )
                                          }
                                          placeholder="DDMMYYYY"
                                          sx={{ width: 110 }}
                                          inputProps={{ maxLength: 8 }}
                                       />
                                    </TableCell>
                                    {/* Bookable Until */}
                                    <TableCell>
                                       <TextField
                                          size="small"
                                          variant="standard"
                                          value={space.bookableUntil ?? ''}
                                          onChange={(e) =>
                                             updateField(
                                                space.sid,
                                                'bookableUntil',
                                                e.target.value,
                                             )
                                          }
                                          placeholder="DDMMYYYY"
                                          sx={{ width: 110 }}
                                          inputProps={{ maxLength: 8 }}
                                       />
                                    </TableCell>
                                    {/* Archive */}
                                    <TableCell align="center">
                                       <Checkbox
                                          checked={!!space.archive}
                                          onChange={(e) =>
                                             updateField(
                                                space.sid,
                                                'archive',
                                                e.target.checked,
                                             )
                                          }
                                          size="small"
                                       />
                                    </TableCell>
                                    {/* Save */}
                                    <TableCell>
                                       {space._dirty && (
                                          <Button
                                             size="small"
                                             variant="contained"
                                             onClick={() => saveRow(space)}
                                             disabled={space._saving}
                                             startIcon={
                                                space._saving ? (
                                                   <CircularProgress size={14} />
                                                ) : (
                                                   <SaveIcon />
                                                )
                                             }
                                          >
                                             Save
                                          </Button>
                                       )}
                                    </TableCell>
                                 </TableRow>
                              ))}
                           </TableBody>
                        </Table>
                     </TableContainer>
                     <TablePagination
                        component="div"
                        count={spaces.length}
                        page={page}
                        onPageChange={(_e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                           setRowsPerPage(parseInt(e.target.value, 10));
                           setPage(0);
                        }}
                        rowsPerPageOptions={[25, 50, 100]}
                     />
                  </>
               )}
            </Box>
         </Drawer>

         {/* Unsaved changes dialog */}
         <Dialog open={discardDialogOpen} onClose={() => setDiscardDialogOpen(false)}>
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogContent>
               <DialogContentText>
                  Some changes have not been saved. Do you want to discard them or
                  continue editing?
               </DialogContentText>
            </DialogContent>
            <DialogActions>
               <Button
                  onClick={() => {
                     setDiscardDialogOpen(false);
                     onClose();
                  }}
               >
                  Discard
               </Button>
               <Button variant="contained" onClick={() => setDiscardDialogOpen(false)}>
                  Continue Editing
               </Button>
            </DialogActions>
         </Dialog>
      </>
   );
};

export default withFirebase(BulkManageDrawerBase);
